import { Article } from '@/constants';

// `article.components` is a JSON scalar with no schema shape (see
// constants.ts's Article doc comment) -- this is the minimal recursive
// shape this walk actually reads, not the full CMS component union (see
// components/article/virtual/component.type.ts for that).
interface ComponentImageNode {
	components?: Array<ComponentImageNode>;
	columns?: Array<Array<ComponentImageNode>>;
	images?: Array<{ imageurl?: string }>;
	imageurl?: string;
}

/**
 * Every image URL referenced by an article's *responsive* body content
 * (`article.components`) -- moved here from `pages/issue/model/
 * issue.hooks.ts`'s `useDownloadAssets` (still the function's original,
 * only caller) so a second consumer, `widgets/issue-reader/menu/
 * menu-button.tsx`'s `DownloadIssueButton` (the explicit "download this
 * issue" action, `PIN_ISSUE_ASSETS`), can reuse the exact same walk instead
 * of a second copy -- a `pages/`-layer module isn't importable from
 * `widgets/`, so this needed a home either could reach downward to.
 *
 * Deliberately excludes replica content: `issue.pages`/`article.replica`
 * (the page-facsimile image arrays) are a separate data shape this walk
 * never reads, which is exactly the property `entities/downloaded-issue`'s
 * `saveDownloadedIssue` doc comment relies on -- replica assets are never
 * downloaded, and this function is *why* collecting "every image this
 * issue needs" naturally already excludes them, with no extra filtering.
 */
export function collectIssueImages(articles: Array<Article>): Array<string> {
	return [...articles.reduce(reduceArticleImages, new Set<string>())];
}

function reduceArticleImages(acc: Set<string>, article: Article) {
	const components = article.components as
		| Array<ComponentImageNode>
		| null
		| undefined;
	if (!components) {
		return acc;
	}
	return components.reduce(reduceComponentImages, acc);
}

function reduceComponentImages(acc: Set<string>, component: ComponentImageNode) {
	const { components, columns, images, imageurl } = component;
	if (components) {
		acc = components.reduce(reduceComponentImages, acc);
	}
	// `columns`/`tabs` components nest their children one level deeper than
	// a plain `components` array: `columns` is an array of columns, each
	// itself an array of components (see Columns.tsx's `columns` prop /
	// Tabs.tsx's `columns[tabIndex]`) -- without this, every image nested
	// inside a column or tab was invisible to the walk.
	if (columns) {
		acc = columns.reduce(
			(acc: Set<string>, column: Array<ComponentImageNode>) =>
				column.reduce(reduceComponentImages, acc),
			acc,
		);
	}
	if (imageurl) {
		acc.add(imageurl);
	}
	// Gallery components carry their images as an array of
	// { imageurl, caption } objects rather than a single `imageurl`.
	if (images) {
		for (const image of images) {
			if (image?.imageurl) {
				acc.add(image.imageurl);
			}
		}
	}
	return acc;
}
